import Head from 'next/head';
import type { AppProps } from 'next/app';
import type { NextComponentType } from 'next';
import { ConfigProvider } from 'antd';
import { Provider } from 'react-redux';
import ReactGA from 'react-ga';
import { store } from '../store';
import { AuthProvider } from '../context/auth';
import { AuthGuard } from '../components/Layouts/AuthGuard';
import ToastLayout from '../components/Layouts/ToastLayout';
import { hotjar } from 'react-hotjar';
import '../styles/globals.css';
import '../components/LongFormat/AgentPlayground/agent-playground.css';   // the Agent Playground screen (every rule under .agent-playground)
import '../components/LongFormat/AgentPlayground/reel/reel.css';   // its answers' reels (.agent-playground .reel)
// import 'antd/dist/antd.css';

import Router, { useRouter } from 'next/router';
import { useEffect, useReducer } from 'react';
import PagesLayout from '@/components/Layouts/PagesLayout';
import PageTransition from '@/components/Layouts/PageTransition';
import posthog from 'posthog-js';
import { ENV } from '@/components/Common/Constants';
import { DashboardProvider } from '@/context/dashboard';
import { HeroUIProvider, ToastProvider } from '@heroui/react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../config/microsoftAuthConfig';
import { Nunito_Sans } from 'next/font/google';

const nunitoSans = Nunito_Sans({
  subsets: ['latin'],
  weight: ['200', '300', '400', '600', '700', '800', '900'],
  variable: '--font-nunito',
  display: 'swap',
});

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.initialize();

if (
  process.env.NEXT_PUBLIC_LOCATION === 'USA' &&
  typeof window !== 'undefined'
) {
  hotjar.initialize(3179575, 6);
  ReactGA.initialize('G-Z9CQXMB3KN');
} else if (
  process.env.NEXT_PUBLIC_LOCATION === 'EUROPE' &&
  typeof window !== 'undefined'
) {
  hotjar.initialize(3179575, 6);
  ReactGA.initialize('G-Z9CQXMB3KN');
}

// PostHog — single US-cloud project for both regions (session replay,
// heatmaps, product analytics + email identify on login). Production only,
// so dev/preview sessions don't pollute the prod project.
if (typeof window !== 'undefined' && ENV === 'production') {
  posthog.init('phc_uxgTFcgwxcrJPLjqK5sPXw9j7arR8B9ReN3MvsG8zYuQ', {
    api_host: 'https://s.enumerate.ai', // managed reverse proxy domain
    ui_host: 'https://us.posthog.com', // proxy in use, so links point back to PostHog
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
  });
}

type CustomAppProps = AppProps & {
  Component: NextComponentType & {
    accessType: 'authenticated' | 'anonymous' | null;
  };
};

function MyApp({ Component, pageProps }: CustomAppProps) {
  const { accessType = null } = Component;
  const router = useRouter();

  // Force _app to re-render after every route change completes. Next.js's
  // static-export client router occasionally fails to propagate the updated
  // router context to React — `useRouter()` returns stale `pathname` or
  // `query` even after the URL bar has updated. Subscribing to the singleton
  // `Router.events.routeChangeComplete` (which always fires with the correct
  // URL) and bumping a local counter forces _app to re-render, at which
  // point `useRouter()` re-reads the (now-updated) context and all children
  // see the fresh route state. This covers both cross-page navigations and
  // within-page query changes (e.g. dashboard tab switches).
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    Router.events.on('routeChangeComplete', forceUpdate);
    Router.events.on('routeChangeError', forceUpdate);
    return () => {
      Router.events.off('routeChangeComplete', forceUpdate);
      Router.events.off('routeChangeError', forceUpdate);
    };
  }, []);

  return (
    <>
      <Head>
        <title>Enumerate AI</title>
      </Head>
      <div className={`${nunitoSans.className} ${nunitoSans.variable}`}>
        <Provider store={store}>
          <MsalProvider instance={msalInstance}>
            <AuthProvider>
              <ToastLayout>
                <ConfigProvider
                  theme={{
                    token: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      colorPrimary: '#5579F2',
                    },
                  }}
                >
                  <HeroUIProvider>
                    <ToastProvider
                      placement="top-right"
                      toastProps={{
                        classNames: {
                          content: 'gap-x-2',
                        },
                      }}
                      toastOffset={16}
                    />
                    <AuthGuard accessType={accessType}>
                      <PageTransition>
                        {router.pathname.startsWith('/setting') ||
                        router.pathname.startsWith('/billing') ||
                        router.pathname.startsWith('/whats-new') ? (
                          <PagesLayout>
                            <Component {...pageProps} />
                          </PagesLayout>
                        ) : router.pathname.startsWith('/dashboard') ? (
                          <DashboardProvider>
                            <Component {...pageProps} />
                          </DashboardProvider>
                        ) : (
                          <Component {...pageProps} />
                        )}
                      </PageTransition>
                    </AuthGuard>
                  </HeroUIProvider>
                </ConfigProvider>
              </ToastLayout>
            </AuthProvider>
          </MsalProvider>
        </Provider>
      </div>
    </>
  );
}

export default MyApp;
